import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../utils/constants/api";
import { apiEndpoints } from "../../../utils/constants/apiEndpoints";
import { ApiResponse } from "../../../types/APIResponse";
import { ConversionTrackingItem } from "../../../types/ConversionTracking";

export const getConversionTracking = createAsyncThunk<
    {
        data: ConversionTrackingItem[];
        counts: number;
        limit: number;
        totalCount: number;
        draftIncompleteCount: number;
        draftCompletedCount: number;
        notPaidCount: number;
        paidCount: number;
        overdueCount: number;
        deletedCount: number;
    },
    {
        offset: number;
        limit: number;
        searchField?: string;
        searchValue?: string;
        fromDate?: string;
        toDate?: string;
        dateField?: string;
        statusFilter?: number | null;
    },
    { rejectValue: string }
>(
    "conversionTracking/getConversionTracking",
    async ({ offset, limit, searchField, searchValue, fromDate, toDate, dateField, statusFilter }, { rejectWithValue }) => {
        try {
            const response = await api.post<
                ApiResponse<ConversionTrackingItem[]> & {
                    counts: number;
                    limit: number;
                    totalCount: number;
                    draftIncompleteCount: number;
                    draftCompletedCount: number;
                    notPaidCount: number;
                    paidCount: number;
                    overdueCount: number;
                    deletedCount: number;
                }
            >(apiEndpoints.lost.getConversionTracking, {
                data: {
                    offset,
                    limit,
                    searchField,
                    searchValue,
                    order: { "lost.\"createdAt\"": "DESC" },
                    fromDate,
                    toDate,
                    dateField,
                    statusFilter: statusFilter !== undefined && statusFilter !== null ? statusFilter : undefined,
                },
            });
            return {
                data: response.data,
                counts: response.counts,
                limit: response.limit,
                totalCount: response.totalCount || 0,
                draftIncompleteCount: response.draftIncompleteCount || 0,
                draftCompletedCount: response.draftCompletedCount || 0,
                notPaidCount: response.notPaidCount || 0,
                paidCount: response.paidCount || 0,
                overdueCount: response.overdueCount || 0,
                deletedCount: response.deletedCount || 0,
            };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch conversion tracking data");
        }
    }
);

export interface ConversionStats {
    statusCounts: Record<number, number>;
    weeklyTrend: Array<{ week: string; total: string; paid: string; revenue: string }>;
    dailyTrend: Array<{ day: string; total: string; paid: string }>;
    monthlyTrend: Array<{ month: string; total: string; paid: string; revenue: string }>;
    topCategories: Array<{ name: string; total: string; paid: string }>;
    topLocations: Array<{ name: string; code: string; total: string; paid: string; revenue: string }>;
    topPlans: Array<{ name: string; planAmount: number; total: string; revenue: string }>;
    deviceBreakdown: Array<{ device: string; total: number }>;
    browserBreakdown: Array<{ browser: string; total: number }>;
    allVisitorData: Array<{ status: number; visitorInfoText: string; amountPaid: number | null }>;
    totalRevenue: number;
}

export const getConversionTrackingStats = createAsyncThunk<
    ConversionStats,
    { fromDate?: string; toDate?: string; dateField?: string },
    { rejectValue: string }
>(
    "conversionTracking/getConversionTrackingStats",
    async ({ fromDate, toDate, dateField }, { rejectWithValue }) => {
        try {
            const response = await api.post<ApiResponse<ConversionStats>>(
                apiEndpoints.lost.getConversionTrackingStats,
                {
                    data: {
                        fromDate,
                        toDate,
                        dateField: fromDate && toDate ? dateField || 'lost."createdAt"' : undefined,
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch conversion stats");
        }
    }
);

// ── Test-claim management (powers the "Manage Test Claims" modal) ──
export interface TestClaimRow {
    id: string;
    trackingId: string;
    status: number;
    isPaid: boolean;
    isTestClaim: boolean;
    createdAt: string;
    customerName: string | null;
    customerEmail: string | null;
    hasSucceededPayment: boolean;
}

export const getClaimsForTestToggle = createAsyncThunk<
    { data: TestClaimRow[]; counts: number; offset: number; limit: number },
    { offset: number; limit: number; searchValue?: string; onlyTest?: boolean; paymentStatus?: 'all' | 'paid' | 'unpaid' },
    { rejectValue: string }
>(
    "conversionTracking/getClaimsForTestToggle",
    async (params, { rejectWithValue }) => {
        try {
            const response: any = await api.post(apiEndpoints.lost.getClaimsForTestToggle, { data: params });
            return {
                data: response.data || [],
                counts: response.counts || 0,
                offset: response.offset ?? params.offset,
                limit: response.limit ?? params.limit,
            };
        } catch (error: any) {
            const serverMsg = error?.response?.data?.message;
            return rejectWithValue(serverMsg || error.message || "Failed to fetch claims");
        }
    }
);

export const toggleTestClaim = createAsyncThunk<
    { id: string; isTestClaim: boolean },
    { id: string; isTestClaim: boolean },
    { rejectValue: string }
>(
    "conversionTracking/toggleTestClaim",
    async ({ id, isTestClaim }, { rejectWithValue }) => {
        try {
            const response: any = await api.put(`${apiEndpoints.lost.toggleTestClaim}/${id}`, {
                data: { isTestClaim },
            });
            return response.data as { id: string; isTestClaim: boolean };
        } catch (error: any) {
            const serverMsg = error?.response?.data?.message;
            return rejectWithValue(serverMsg || error.message || "Failed to update test flag");
        }
    }
);
